<script setup lang="ts">
import { ref } from 'vue';
import { RouterLink } from 'vue-router';
import { Check, Sparkles } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import PlanTicket from '@app/presentation/components/payments/PlanTicket.vue';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';

useHead({
  title: 'Цены — Таро Мяу | Бесплатные и Премиум Расклады',
  meta: [
    { name: 'description', content: 'Бесплатные расклады Таро: карта дня, да/нет, одна карта. Премиум подписка от 222₽/неделю — доступ ко всем 16 раскладам и чату с тарологом.' },
    { name: 'keywords', content: 'таро цены, таро подписка, расклад таро бесплатно, таро премиум' },
    { property: 'og:title', content: 'Цены — Таро Мяу' },
    { property: 'og:description', content: 'Бесплатные расклады Таро и премиум подписка от 222₽/неделю.' },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Цены — Таро Мяу' },
    { name: 'twitter:description', content: 'Бесплатные расклады Таро и премиум подписка от 222₽/неделю.' },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/ceny' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/ceny' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/ceny' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Таро Мяу Премиум',
        description: 'Доступ ко всем 16 раскладам Таро с персональной интерпретацией',
        brand: { '@type': 'Brand', name: 'Таро Мяу' },
        image: 'https://tarotmeow.ru/android-chrome-512x512.png',
        offers: [
          { '@type': 'Offer', name: '1 неделя', price: '222', priceCurrency: 'RUB', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: '1 месяц', price: '555', priceCurrency: 'RUB', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: '1 год', price: '999', priceCurrency: 'RUB', availability: 'https://schema.org/InStock' },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Цены', item: 'https://tarotmeow.ru/ceny' },
        ],
      }),
    },
  ],
});

const freeTier = [
  'Карта дня',
  'Расклад «Да или Нет»',
  'Расклад на одну карту',
  'Расклад на три карты',
  'Обучение картам',
  'История раскладов',
];

const premiumTier = [
  'Все 16 видов раскладов',
  'Любовный расклад',
  'Финансовый расклад',
  'Расклад Судьбы',
  'Чат с тарологом без ограничений',
  'Приоритетная поддержка',
];

const plans = [
  { period: '1 неделя', price: '222', originalPrice: '278' },
  { period: '1 месяц', price: '555', originalPrice: '694', popular: true },
  { period: '1 год', price: '999', originalPrice: '1249', bestValue: true },
];

// The plans are the app's theatre tickets (PlanTicket, as on the paywall): pick one to
// compare — the popular month is picked to start with (static, so the prerender matches).
const selectedPlan = ref('1 месяц');
</script>

<template>
  <div class="site-container">
    <!-- Header -->
    <section class="py-16 text-center md:py-24">
      <ScrollReveal>
        <div class="type-overline mb-3.5 text-primary-ink">
          Цены и подписка
        </div>
        <h1 class="type-display">Цены</h1>
        <p class="mx-auto mt-4 max-w-2xl text-[17px] leading-relaxed text-muted-foreground">
          Начни бесплатно — 4 расклада доступны без подписки
        </p>
      </ScrollReveal>
    </section>

    <!-- Tiers: a plain sheet of stock, and a patch of plum velvet for premium. -->
    <section class="grid gap-6 md:grid-cols-2">
      <!-- Free -->
      <ScrollReveal :delay="80">
        <div class="paper-sheet flex h-full flex-col gap-6 p-8">
          <div>
            <h2 class="type-heading">Бесплатно</h2>
            <p class="mt-1 text-muted-foreground">Всегда доступно</p>
          </div>
          <div class="font-serif text-4xl">0 ₽</div>
          <ul class="space-y-3">
            <li
              v-for="item in freeTier"
              :key="item"
              class="flex items-center gap-3"
            >
              <span
                class="tier-tick flex h-6 w-6 flex-shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                <Check
                  class="h-3.5 w-3.5"
                  :stroke-width="2.5"
                />
              </span>
              <span class="text-[15px]">{{ item }}</span>
            </li>
          </ul>
          <Button
            as-child
            variant="outline"
            size="lg"
            class="mt-auto w-full"
          >
            <a
              :href="TELEGRAM_BOT_URL"
              target="_blank"
              rel="noopener"
            >
              {{ TELEGRAM_CTA_LABEL }}
            </a>
          </Button>
        </div>
      </ScrollReveal>

      <!-- Premium -->
      <ScrollReveal :delay="160">
        <!-- [background-size:auto]: .paper-sheet sizes its layers to the 512px grain tile, so on a
             sheet wider than that the fabric would repeat with a visible seam. -->
        <div class="paper-sheet fabric-velvet relative flex h-full flex-col gap-6 bg-stock-plum p-8 text-foreground [background-size:auto]">
          <span class="tier-stamp type-overline absolute -top-3 right-6 z-[2] inline-flex items-center gap-1 bg-stock-wine px-3 py-1">
            <Sparkles
              class="h-3 w-3"
              aria-hidden="true"
            /> Популярно
          </span>
          <div class="relative z-[1]">
            <h2 class="type-heading">Премиум</h2>
            <p class="mt-1 opacity-85">Полный доступ ко всему</p>
          </div>
          <div class="relative z-[1]">
            <span class="font-serif text-4xl">от 222 ₽</span>
            <span class="opacity-85"> / неделя</span>
          </div>
          <ul class="relative z-[1] space-y-3">
            <li
              v-for="item in premiumTier"
              :key="item"
              class="flex items-center gap-3"
            >
              <span
                class="tier-tick flex h-6 w-6 flex-shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                <Check
                  class="h-3.5 w-3.5"
                  :stroke-width="2.5"
                />
              </span>
              <span class="text-[15px]">{{ item }}</span>
            </li>
          </ul>
          <Button
            as-child
            size="lg"
            variant="secondary"
            class="relative z-[1] mt-auto w-full"
          >
            <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
          </Button>
        </div>
      </ScrollReveal>
    </section>

    <!-- Plans: theatre tickets -->
    <section class="py-16 md:py-24">
      <ScrollReveal class="mb-8 text-center">
        <h2 class="type-title">Планы подписки</h2>
      </ScrollReveal>
      <ScrollReveal :delay="80">
        <div
          class="mx-auto grid max-w-[560px] grid-cols-3 gap-3 pt-5 md:gap-5"
          role="group"
          aria-label="Планы подписки"
        >
          <PlanTicket
            v-for="(plan, i) in plans"
            :key="plan.period"
            :period="plan.period"
            :price="plan.price"
            :original-price="plan.originalPrice"
            :stamp="plan.popular
              ? { label: 'Популярно', tone: 'wine' }
              : plan.bestValue ? { label: 'Выгодно', tone: 'moss' } : undefined"
            :selected="selectedPlan === plan.period"
            :index="i"
            @click="selectedPlan = plan.period"
          />
        </div>
      </ScrollReveal>
    </section>

    <!-- CTA: a raised sheet with the long cat stretched out along its top edge. -->
    <section class="pb-16 pt-10 md:pb-24">
      <ScrollReveal>
        <div class="paper-sheet-raised relative mx-auto max-w-3xl px-8 pb-10 pt-16 text-center md:px-14 md:pb-14 md:pt-20">
          <div
            class="pointer-events-none absolute -top-[76px] left-1/2 w-[200px] -translate-x-1/2 md:-top-[92px] md:w-[240px]"
            aria-hidden="true"
          >
            <PaperCutout
              name="long_cat"
              lazy
            />
          </div>
          <h2 class="type-title">Начни с бесплатного расклада</h2>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              size="lg"
              variant="outline"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
          </div>
        </div>
      </ScrollReveal>
    </section>
  </div>
</template>

<style scoped>
/* A tick on a small hand-cut cream disc. */
.tier-tick {
  border-radius: 52% 48% 50% 46% / 48% 52% 46% 50%;
  background: var(--paper-stock);
  color: var(--paper-stock-ink);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 2px 4px -1px var(--paper-shade);
}
/* "Popular": a stamp stuck on the premium patch's corner (as PlanTicket's). */
.tier-stamp {
  border-radius: var(--paper-radius-xs);
  color: var(--foreground);
  transform: rotate(3deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.18), 0 3px 6px -2px var(--paper-shade);
}
</style>
